import { formatDate, type SettingView, type SettingsGroup } from "@elektr/shared";
import { useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/card";
import { Field, Input, Select } from "@/components/ui/form";
import { ApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import {
  CHOICE_LABEL,
  FIELD_LABEL,
  GROUP_HINT,
  isVisible,
  type FieldKey,
  type GroupName,
} from "@/lib/settingsFields";

export interface VerifyOutcome {
  ok: boolean | null;
  message: string;
}

type Values = Record<string, string | null>;

/** Bir guruh sozlamalari: maxfiylari niqoblangan, "Saqlash" va "Tekshirish" (saqlamasdan sinash) tugmalari bilan. */
export function GroupForm({
  group,
  data,
  onSave,
  onVerify,
  extra,
}: {
  group: GroupName;
  data: SettingsGroup;
  onSave: (values: Values) => Promise<unknown>;
  onVerify: (values: Record<string, string>) => Promise<VerifyOutcome>;
  extra?: ReactNode;
}) {
  const { t } = useT();
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<VerifyOutcome | null>(null);
  const [busy, setBusy] = useState<"save" | "verify" | null>(null);

  const byKey = new Map(data.settings.map((s) => [s.key, s]));
  const current = (k: string): string =>
    edited[k] ?? (byKey.get(k)?.secret ? "" : (byKey.get(k)?.value ?? ""));
  const dirty = Object.keys(edited).length > 0;

  const set = (k: string, v: string) => {
    setEdited((e) => ({ ...e, [k]: v }));
    setResult(null);
  };

  async function run(kind: "save" | "verify", fn: () => Promise<void>) {
    setBusy(kind);
    try {
      await fn();
    } catch (e) {
      const body =
        e instanceof ApiError
          ? (e.body as { errors?: Record<string, string> } | undefined)
          : undefined;
      setErrors(body?.errors ?? { _: t("common.error") });
    } finally {
      setBusy(null);
    }
  }

  const save = (values: Values = edited) =>
    run("save", async () => {
      setErrors({});
      await onSave(values);
      setEdited({});
    });

  const check = () =>
    run("verify", async () => {
      setErrors({});
      setResult(await onVerify(edited));
    });

  const shown =
    result ?? (data.check ? { ok: data.check.ok ?? null, message: data.check.message } : null);

  function renderField(s: SettingView) {
    const label = t(FIELD_LABEL[s.key as FieldKey] ?? "settings.title");
    const err = errors[s.key] ?? null;
    if (s.readonly) return null;
    if (s.kind === "bool") {
      return (
        <label key={s.key} className="flex min-h-11 items-center gap-3 text-sm font-medium">
          <input
            type="checkbox"
            className="size-5"
            checked={current(s.key) === "true"}
            onChange={(e) => set(s.key, e.target.checked ? "true" : "false")}
          />
          {label}
        </label>
      );
    }
    if (s.kind === "choice") {
      return (
        <Field key={s.key} label={label} error={err}>
          {(id) => (
            <Select id={id} value={current(s.key)} onChange={(e) => set(s.key, e.target.value)}>
              {s.choices.map((c) => (
                <option key={c} value={c}>
                  {t(CHOICE_LABEL[c as keyof typeof CHOICE_LABEL])}
                </option>
              ))}
            </Select>
          )}
        </Field>
      );
    }
    const hint =
      s.secret && s.set
        ? t("settings.secret_hint", { value: s.value })
        : s.source === "env" && !s.secret
          ? t("settings.from_env")
          : undefined;
    return (
      <Field key={s.key} label={label} hint={hint} error={err}>
        {(id) => (
          <div className="flex gap-2">
            <Input
              id={id}
              type={s.secret ? "password" : s.kind === "int" ? "number" : "text"}
              autoComplete="off"
              value={current(s.key)}
              placeholder={s.secret && s.set ? s.value : undefined}
              onChange={(e) => set(s.key, e.target.value)}
            />
            {s.secret && s.set && s.source === "db" && (
              <Button
                type="button"
                variant="outline"
                onClick={() => void save({ [s.key]: null })}
                aria-label={`${t("settings.remove")}: ${label}`}
              >
                {t("settings.remove")}
              </Button>
            )}
          </div>
        )}
      </Field>
    );
  }

  const username = byKey.get("telegram.bot_username");
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (dirty) void save();
      }}
    >
      <p className="text-sm text-muted-foreground">{t(GROUP_HINT[group])}</p>
      {data.settings.filter((s) => isVisible(s.key, current)).map(renderField)}
      {group === "telegram" && username?.set && <p className="text-sm">@{username.value}</p>}
      {errors._ && (
        <p role="alert" className="text-sm text-danger">
          {errors._}
        </p>
      )}
      {extra}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <Button type="submit" disabled={!dirty || busy !== null}>
          {t("settings.save")}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => void check()}
          disabled={busy !== null}
        >
          {busy === "verify" ? t("settings.verifying") : t("settings.verify")}
        </Button>
        {shown && (
          <div
            role="status"
            className="flex flex-wrap items-center gap-2 text-sm"
            data-testid={`check-${group}`}
          >
            <Badge tone={shown.ok === true ? "success" : shown.ok === false ? "danger" : "warning"}>
              {shown.ok === true
                ? t("settings.check_ok")
                : shown.ok === false
                  ? t("settings.check_fail")
                  : t("settings.check_unknown")}
            </Badge>
            <span>{shown.message}</span>
            {!result && data.check && (
              <span className="text-xs text-muted-foreground">
                {t("settings.last_check", { date: formatDate(data.check.checked_at) })}
              </span>
            )}
          </div>
        )}
      </div>
    </form>
  );
}
